<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";
import { isCompanyAdminFromToken } from "../../utils/authRole";
import { useCompanyKyc, kycStatusFlag } from "../../composables/useCompanyKyc";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const ensureAdminAccess = () => {
  if (!isCompanyAdminFromToken(localStorage.getItem("_panda"))) {
    router.replace("/request");
    return false;
  }
  return true;
};

if (!ensureAdminAccess()) {
  // Redirected in ensureAdminAccess.
}

interface RuleForm {
  name: string;
  email: string;
  phone_no: string;
  contact_person: string;
}

const formError = ref("");
const results = ref();
const loading = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({
  name: "",
  phone_no: "",
  email: "",
  contact_person: "",
});

const rules = reactive<FormRules<RuleForm>>({
  name: [
    {
      required: true,
      message: "Please enter the company name",
      trigger: "blur",
    },
  ],
  phone_no: [
    {
      required: true,
      message: "Please enter the phone number",
      trigger: "blur",
    },
  ],
  email: [
    {
      required: true,
      message: "Please enter the business email",
      trigger: "blur",
    },
  ],
  contact_person: [
    {
      required: true,
      message: "Please enter the contact person's name",
      trigger: "blur",
    },
  ],
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      formError.value = "";
      postData();
    }
  });
};

const initializeForm = () => {
  const userData = store.getUserData || {};
  ruleForm.name = userData.company_name || "";
  ruleForm.contact_person = userData.user_name || "";
  ruleForm.email = userData.user_email || userData.email || "";
  ruleForm.phone_no = userData.user_phone || "";
};

initializeForm();

const postData = async () => {
  if (!ensureAdminAccess()) return;
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}updateuser?token=${store.getToken}`,
      {
        email: ruleForm.email,
        name: ruleForm.contact_person,
        phone: ruleForm.phone_no,
        company_name: ruleForm.name,
        company_id: store.getUserData.company_id,
        user_id: store.getUserData.user_id,
        user_type: store.getUserData.user_type,
      },
    );

    loading.value = false;
    if (response.status === 200 && response.data.status) {
      ElMessage({
        message: "Business profile updated successfully",
        type: "success",
      });
      updateStore();
      router.push("/account/business");
    } else {
      const message =
        response.data?.message || "Business profile update failed";
      ElMessage({ message, type: "error" });
    }
  } catch {
    loading.value = false;
    formError.value = "Something went wrong, please try again.";
  }
};

// Registration details and verification state are read-only here: they come
// from signup and are checked during verification.
const kyc = useCompanyKyc();
onMounted(() => void kyc.load());
const company = computed(() => kyc.view.value?.company || null);
const accountLabel = computed(() => {
  const t = company.value?.account_type;
  return t === "retail" ? "Personal account" : t === "business" ? "Business account" : "";
});
const kycFlag = computed(() => kycStatusFlag(kyc.status.value));
const hotline = import.meta.env.VITE_HOTLINE;
const signedUp = computed(() => {
  const d = company.value?.date_signed_up;
  if (!d) return "";
  const date = new Date(d);
  return isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
});

const updateStore = () => {
  store.setUserData({
    ...store.getUserData,
    company_name: ruleForm.name,
    user_name: ruleForm.contact_person,
    user_email: ruleForm.email,
    email: ruleForm.email,
    user_phone: ruleForm.phone_no,
  });
};
</script>

<template>
  <div class="ac-card" v-loading="loading">
    <div class="ac-hero">
      <div class="ac-hero-mark">
        {{ ruleForm.name?.charAt(0)?.toUpperCase() || "B" }}
      </div>
      <div class="ac-hero-info">
        <div class="ac-hero-name">{{ ruleForm.name || "Your Company" }}</div>
        <div class="ac-hero-email">{{ ruleForm.email || "company@email.com" }}</div>
        <div class="ac-hero-badges">
          <span v-if="accountLabel" class="ac-hero-badge">{{ accountLabel }}</span>
          <router-link
            v-if="kycFlag && kyc.isBusiness.value"
            to="/account/verification"
            class="ac-hero-badge"
            :class="{
              'ac-hero-badge--ok': kycFlag.tone === 'ok',
              'ac-hero-badge--warn': kycFlag.tone === 'warn' || kycFlag.tone === 'bad',
            }"
            >{{ kycFlag.label }}</router-link
          >
        </div>
      </div>
      <div v-if="ruleForm.contact_person" class="ac-hero-aside">
        <div class="ac-hero-aside-label">Primary contact</div>
        <div class="ac-hero-aside-value">{{ ruleForm.contact_person }}</div>
      </div>
    </div>

    <div class="ac-card-body">
      <template v-if="company">
        <div class="ac-section-label">Registration</div>
        <dl class="ac-facts">
          <div v-if="company.account_type === 'business'" class="ac-fact">
            <dt>Registration number</dt>
            <dd>{{ company.registration_number || "Not provided" }}</dd>
          </div>
          <div v-if="company.account_type === 'business'" class="ac-fact">
            <dt>KRA PIN</dt>
            <dd>{{ company.kra_pin || "Not provided" }}</dd>
          </div>
          <div v-if="accountLabel" class="ac-fact">
            <dt>Account type</dt>
            <dd>{{ accountLabel }}</dd>
          </div>
          <div v-if="signedUp" class="ac-fact">
            <dt>Customer since</dt>
            <dd>{{ signedUp }}</dd>
          </div>
        </dl>
        <p v-if="company.account_type === 'business'" class="ac-note bp-note">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
          <span>
            Given at signup and checked during
            <router-link to="/account/verification">verification</router-link>.
            <template v-if="hotline">To correct them, call {{ hotline }}.</template>
          </span>
        </p>
      </template>

      <div class="ac-error" v-if="formError">{{ formError }}</div>

      <el-form
        ref="ruleFormRef"
        :model="ruleForm"
        :rules="rules"
        label-width="0px"
        status-icon
      >
        <div class="ac-section-label">Company information</div>
        <div class="ac-grid">
          <div class="ac-field">
            <label class="ac-label" for="biz-name"
              >Company Name <span class="ac-req">*</span></label
            >
            <el-form-item prop="name" class="ac-fi">
              <input
                id="biz-name"
                v-model="ruleForm.name"
                class="ac-input"
                placeholder="Your company name"
              />
            </el-form-item>
          </div>

          <div class="ac-field">
            <label class="ac-label" for="biz-email"
              >Business Email <span class="ac-req">*</span></label
            >
            <el-form-item prop="email" class="ac-fi">
              <input
                id="biz-email"
                v-model="ruleForm.email"
                class="ac-input"
                type="email"
                placeholder="company@email.com"
              />
            </el-form-item>
          </div>
        </div>

        <div class="ac-section-label">Contact details</div>
        <div class="ac-grid">
          <div class="ac-field">
            <label class="ac-label" for="biz-contact"
              >Contact Person <span class="ac-req">*</span></label
            >
            <el-form-item prop="contact_person" class="ac-fi">
              <input
                id="biz-contact"
                v-model="ruleForm.contact_person"
                class="ac-input"
                placeholder="Primary contact name"
              />
            </el-form-item>
          </div>

          <div class="ac-field">
            <label class="ac-label"
              >Phone Number <span class="ac-req">*</span></label
            >
            <el-form-item prop="phone_no" class="ac-fi">
              <MazPhoneNumberInput
                v-model="ruleForm.phone_no"
                defaultCountryCode="KE"
                :only-countries="['KE', 'UG', 'TZ', 'NG', 'ZA']"
                @update="results = $event"
                :success="results?.isValid"
                class="ac-phone"
              />
            </el-form-item>
          </div>
        </div>

        <div class="ac-actions">
          <button
            class="ac-btn"
            @click.prevent="submitForm(ruleFormRef)"
            :disabled="loading"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Save Changes
          </button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<style scoped>
.bp-note {
  margin: 10px 0 26px;
}
.bp-note a {
  color: #12284a;
  font-weight: 600;
}
</style>
